import React, { useState, useRef } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { url } from '../../util/config';

const MAX_TITLE = 50;
const MIN_TITLE = 5;
const MAX_DESC = 500;
const MIN_DESC = 10;
const MAX_TAG = 10;
const MIN_TAG = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ['application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

const CreateDataConverseComponent = () => {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [desc, setDesc] = useState('');
    const [tag, setTag] = useState('');
    const [file, setFile] = useState(null);

    const [titleError, setTitleError] = useState('');
    const [descError, setDescError] = useState('');
    const [tagError, setTagError] = useState('');
    const [fileError, setFileError] = useState('');
    const [loading, setLoading] = useState(false);

    const fileInputRef = useRef();

    // Validation handlers
    const handleTitleChange = (e) => {
        const val = e.target.value;
        setTitle(val);
        if (!val.trim()) setTitleError('Title is required');
        else if (val.length < MIN_TITLE) setTitleError(`Min ${MIN_TITLE} characters required`);
        else if (val.length > MAX_TITLE) setTitleError(`Max ${MAX_TITLE} characters allowed`);
        else setTitleError('');
    };

    const handleDescChange = (e) => {
        const val = e.target.value;
        setDesc(val);
        if (!val.trim()) setDescError('Description is required');
        else if (val.length < MIN_DESC) setDescError(`Min ${MIN_DESC} characters required`);
        else if (val.length > MAX_DESC) setDescError(`Max ${MAX_DESC} characters allowed`);
        else setDescError('');
    };

    const handleTagChange = (e) => {
        const val = e.target.value;
        setTag(val);
        if (!val.trim()) setTagError('Tag is required');
        else if (val.length < MIN_TAG) setTagError(`Min ${MIN_TAG} characters required`);
        else if (val.length > MAX_TAG) setTagError(`Max ${MAX_TAG} characters allowed`);
        else setTagError('');
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        validateFile(selectedFile);
    };

    const validateFile = (selectedFile) => {
        if (!selectedFile) {
            setFile(null);
            setFileError('File is required');
            return;
        }
        if (!ALLOWED_TYPES.includes(selectedFile.type)) {
            setFile(null);
            setFileError('Invalid file type. Allowed: .pdf, .txt, .doc, .docx');
            return;
        }
        if (selectedFile.size > MAX_FILE_SIZE) {
            setFile(null);
            setFileError('File size exceeds 5 MB');
            return;
        }
        setFile(selectedFile);
        setFileError('');
    };

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            validateFile(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Final validation
        if (!title.trim() || title.length < MIN_TITLE || title.length > MAX_TITLE) {
            setTitleError('Title is required and must be between 5 and 50 characters.');
            return;
        }
        if (!desc.trim() || desc.length < MIN_DESC || desc.length > MAX_DESC) {
            setDescError('Description is required and must be between 10 and 500 characters.');
            return;
        }
        if (!tag.trim() || tag.length < MIN_TAG || tag.length > MAX_TAG) {
            setTagError('Tag is required and must be between 3 and 10 characters.');
            return;
        }
        if (!file) {
            setFileError('File is required.');
            return;
        }
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('description', desc);
            formData.append('metatag', tag);
            formData.append('file', file);

            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/dataconverse`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });
            if (!response.ok) throw new Error('Failed to create Data Converse');
            toast.success('Data Converse created successfully!', { autoClose: 2000 });
            setTimeout(() => {
                navigate('/dataconverse');
            }, 2000);
        } catch (error) {
            toast.error(error.message || 'Error creating Data Converse', { autoClose: 2000 });
        }
        setLoading(false);
    };

    return (
        <WrapperComponent>
            <div className="container mt-4">
                <ToastContainer />
                <h2 className="mb-4">Create Data Converse AI</h2>
                <form onSubmit={handleSubmit} className="card p-4 shadow-sm">
                    {/* Title */}
                    <div className="mb-3">
                        <label className="form-label fw-bold">Title <span className="text-danger">*</span></label>
                        <input
                            type="text"
                            className={`form-control ${titleError ? 'is-invalid' : ''}`}
                            value={title}
                            onChange={handleTitleChange}
                            maxLength={MAX_TITLE}
                            placeholder="Enter title"
                        />
                        <div className="d-flex justify-content-end">
                            <small className={titleError ? 'text-danger' : 'text-muted'}>
                                {titleError ? titleError : `${title.length}/${MAX_TITLE}`}
                            </small>
                        </div>
                    </div>
                    {/* Description */}
                    <div className="mb-3">
                        <label className="form-label fw-bold">Description <span className="text-danger">*</span></label>
                        <textarea
                            className={`form-control ${descError ? 'is-invalid' : ''}`}
                            value={desc}
                            onChange={handleDescChange}
                            rows={4}
                            maxLength={MAX_DESC}
                            placeholder="Enter description"
                        />
                        <div className="d-flex justify-content-end">
                            <small className={descError ? 'text-danger' : 'text-muted'}>
                                {descError ? descError : `${desc.length}/${MAX_DESC}`}
                            </small>
                        </div>
                    </div>
                    {/* Tag */}
                    <div className="mb-3">
                        <label className="form-label fw-bold">Tag <span className="text-danger">*</span></label>
                        <input
                            type="text"
                            className={`form-control ${tagError ? 'is-invalid' : ''}`}
                            value={tag}
                            onChange={handleTagChange}
                            maxLength={MAX_TAG}
                            placeholder="Enter tag"
                        />
                        <div className="d-flex justify-content-end">
                            <small className={tagError ? 'text-danger' : 'text-muted'}>
                                {tagError ? tagError : `${tag.length}/${MAX_TAG}`}
                            </small>
                        </div>
                    </div>
                    {/* File Upload */}
                    <div className="mb-3">
                        <label className="form-label fw-bold">File Upload <span className="text-danger">*</span></label>
                        <div
                            className={`border rounded p-3 mb-2 ${fileError ? 'border-danger' : 'border-secondary'}`}
                            style={{ background: '#f8f9fa', cursor: 'pointer' }}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current.click()}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept=".pdf,.txt,.doc,.docx"
                                onChange={handleFileChange}
                            />
                            <div>
                                {file
                                    ? <span className="text-success">{file.name}</span>
                                    : <span className="text-muted">Drag & drop or click to select a file (.pdf, .txt, .doc, .docx, max 5MB)</span>
                                }
                            </div>
                        </div>
                        <small className={fileError ? 'text-danger' : 'text-muted'}>
                            {fileError ? fileError : ''}
                        </small>
                    </div>
                    {/* Submit */}
                    <div className="d-flex justify-content-end">
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </form>
            </div>
        </WrapperComponent>
    );
};

export default CreateDataConverseComponent;